import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import '@fortawesome/fontawesome-free/css/all.min.css';

// 🔷 PrimeReact Provider Import
import { PrimeReactProvider } from 'primereact/api';
import ProtectedRoute from './components/ProtectedRoute';
import AdminProtectedRoute from './components/AdminProtectedRoute';

// Pages
import App from './App';
import About from './pages/About';
import Plans from './pages/Plans';
import ContactPage from './pages/ContactPage';
import GetStarted from './pages/GetStarted';
import Login from './pages/Login';
import PrivacyPolicy from './pages/PrivacyPolicy';
import ForgotPassword from './pages/ForgotPassword'; 
import CreatePassword from './pages/CreatePassword';
import ResetPassword from './pages/ResetPassword';
import VerifyAccount from './pages/VerifyAccount';
import VerificationCompletionPage from './pages/VerificationCompletionPage';
import BusinessInfo from './pages/BusinessInfo';
import ScheduleActivationCall from './pages/ScheduleActivationCall';
import BusinessDashboard from './pages/BusinessDashboard';
import OnboardingBusinessDashboard from './pages/OnboardingBusinessDashboard';
import InsuranceLinks from './pages/InsuranceLinks';
import Products from './pages/Products';       
import ProductDetails from './pages/ProductDetails';

// HMO Flow
import ApplyForPlan from './pages/Hmo/ApplyForPlan';
import InsuranceLanding from './pages/Hmo/InsuranceLanding';
import ProductDetail from './pages/Hmo/ProductDetail';
import ProductSelection from './pages/Hmo/ProductSelection';
import PlanOwnerDetails from './pages/Hmo/PlanOwnerDetails';
import CoverageDetails from './pages/Hmo/CoverageDetails';
import AgentManagement from './pages/Hmo/AgentManagement';

// ✅ Admin Pages
import AdminDashboard from './pages/Admin/AdminDashboard';
import AdminLogin from './pages/Admin/AdminLogin';
import ProductPlans from './pages/Admin/ProductPlans';
import Hospitals from './pages/Admin/Hospitals';
import Companies from './pages/Admin/Companies';
import CompanyHmoStaff from './pages/Admin/CompanyHmoStaff';
import AdminCRUDPage from './pages/Admin/AdminCRUDPage';
import ViewCompany from './pages/Admin/ViewCompany';
import FeedbackForm from './pages/Hmo/FeedbackForm';
import StaffDependentsView from './pages/Hmo/StaffDependentsView';

// Settings
import Settings from './pages/Settings';
import OnboardSettings from './pages/OnboardSettings';

// Agent
import AgentLogin from './pages/Agent/AgentLogin';
import AgentDashboard from './pages/Agent/AgentDashboard';
import HmoLinks from './pages/Agent/HmoLinks';
import PaymentSuccess from './pages/Hmo/PaymentSuccess';
// import ProductPlanPage from './pages/Admin/ProductPlanPage';
import CompanyDocuments from './pages/Admin/CompanyDocuments';
import UserCompanyDocuments from './pages/UserCompanyDocuments';

// 🚨 NEW: Import Error Pages
import BadRequest from './pages/errors/BadRequest';
import Unauthorized from './pages/errors/Unauthorized';
import Forbidden from './pages/errors/Forbidden';
import NotFound from './pages/errors/NotFound';
import ServerError from './pages/errors/ServerError';
import ServiceUnavailable from './pages/errors/ServiceUnavailable';
import LandingPage from './pages/Admin/LandingPage';
// Styles
import 'bootstrap/dist/css/bootstrap.min.css';
import 'aos/dist/aos.css';
import './index.css';

// 🔷 PrimeReact Theme Imports
import 'primereact/resources/themes/lara-light-indigo/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';

// 🔷 Chart.js Registration
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import ChangePasswordForm from './pages/Admin/ChangePasswordForm';
import PlanCategories from './frontend/components/PlanCategories';
import AllPlans from './frontend/components/AllPlans';
import PlanSelectionPage from './frontend/PlanSelectionPage';
import EnrollmentPage from './frontend/EnrollmentPage';
import FamilyGroupEnrollment from './frontend//FamilyGroupEnrollment';
import SomeoneElseEnrollment from './frontend/SomeoneElseEnrollment';
import MyselfEnrollment from './frontend/MyselfEnrollment';
import SuccessPage from './pages/SuccessPage';


ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// 🔷 Optional: Configure PrimeReact Global Settings
const primeReactConfig = {
  ripple: true,              
  inputStyle: 'outlined',    
  tooltipOptions: {
    showDelay: 300,
    hideDelay: 250,
  },
  cssTransition: {
    classNames: {
      dialog: {
        enter: 'p-dialog-enter',
        enterActive: 'p-dialog-enter-active',
        exit: 'p-dialog-exit',
        exitActive: 'p-dialog-exit-active',
      },
    },
    timeout: { enter: 300, exit: 250 },
  },
};

const root = ReactDOM.createRoot(document.getElementById('root'));

root.render(
  <React.StrictMode>
    {/* 🔷 PrimeReactProvider wraps the entire app */}
    <PrimeReactProvider value={primeReactConfig}>
      <BrowserRouter>
        <Routes>
          {/* Landing Page */}
          <Route path="/" element={<App />} />
          <Route path="/about" element={<About />} />
          <Route path="/plans" element={<Plans />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/feedback" element={<FeedbackForm />} />
           <Route path="/planselect" element={<PlanSelectionPage />} />
           <Route path="/enrollment" element={<EnrollmentPage />} />
          <Route path="/enrollment/myself" element={<MyselfEnrollment />} />
          <Route path="/enrollment/someone-else" element={<SomeoneElseEnrollment />} />
           <Route path="/enrollment/family-group" element={<FamilyGroupEnrollment />} />
          {/* Default route redirects to categories, or you can set it to '/' */}
            <Route path="/payment/success" element={<EnrollmentPage />} />
         <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route path="/success" element={<SuccessPage />} />
        <Route path="/categories" element={<PlanCategories />} />
        <Route path="/allplans" element={<AllPlans />} />
          {/* Authentication Flow */}
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/get-started" element={<GetStarted />} />
          <Route path="/create-password" element={<CreatePassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/staff/:staffId/dependents" element={<StaffDependentsView />} />
          <Route path="/verify-account" element={<VerifyAccount />} />
          <Route path="/verification-complete" element={<VerificationCompletionPage />} />
          
          {/* Onboarding Flow */}
          <Route path="/business-info" element={<BusinessInfo />} />
          <Route path="/schedule-activation" element={<ScheduleActivationCall />} />
          
          <Route element={<ProtectedRoute />}>
             
            <Route path="/dashboard" element={<BusinessDashboard />} />
            <Route path="/onboarding-dashboard" element={<OnboardingBusinessDashboard />} />
            <Route path="/company/documents" element={<UserCompanyDocuments />} />
            <Route path="/agents" element={<AgentManagement />} />
            {/* Other Pages */}
            <Route path="/insurance-links" element={<InsuranceLinks />} />
            <Route path="/products" element={<Products />} />
            <Route path="/products/:id" element={<ProductDetails />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/OnboardSettings" element={<OnboardSettings />} />

            {/* HMO Public Flow */}
            <Route path="/insurance/landing" element={<ApplyForPlan />} />
            <Route path="/insurance/landing/:linkId" element={<ApplyForPlan />} />
            <Route path="/insurance/buy" element={<InsuranceLanding />} />
            <Route path="/insurance/renew" element={<ApplyForPlan />} />
            <Route path="/insurance/manage" element={<ApplyForPlan />} />
            <Route path="/insurance/products" element={<ProductSelection />} />
            <Route path="/insurance/product-details" element={<ProductDetail />} />
            <Route path="/insurance/plan-owner" element={<PlanOwnerDetails />} />
            <Route path="/insurance/coverage" element={<CoverageDetails />} />
          </Route>
        
          {/* ✅ Admin Routes */}
          <Route path="/admin-login" element={<AdminLogin />} />
           <Route path="/changepassword" element={<ChangePasswordForm />} />
        <Route path="/ResetPassword" element={<ResetPassword />} />
          <Route element={<AdminProtectedRoute />}>
          <Route path="/landing" element={<LandingPage />} />
            <Route path="/hmoadmin" element={<AdminDashboard />} />
            <Route path="/admin/product-plans" element={<ProductPlans />} />
            <Route path="/admin/hospitals" element={<Hospitals />} />
            <Route path="/admin/companies" element={<Companies />} />
            <Route path="/admin/companies/:companyId/view" element={<ViewCompany />} />
            <Route path="/admin/company-documents" element={<CompanyDocuments />} />
            <Route path="/admin/companies/:companyId/hmo-staff" element={<CompanyHmoStaff />} />
            <Route path="/admin/crud/:entity" element={<AdminCRUDPage />} />
          </Route>
        
          <Route path="/agent-login" element={<AgentLogin />} />
          <Route path="/agent-dashboard" element={<AgentDashboard />} />
          <Route path="/agent-links" element={<HmoLinks />} />
          <Route path="/insurance/payment-success" element={<PaymentSuccess />} />
          
          {/* ========================================== */}
          {/* 🚨 ERROR PAGES ROUTES                      */}
          {/* ========================================== */}
          <Route path="/400" element={<BadRequest />} />
          <Route path="/401" element={<Unauthorized />} />
          <Route path="/403" element={<Forbidden />} />
          <Route path="/500" element={<ServerError />} />
          <Route path="/503" element={<ServiceUnavailable />} />
          
          {/* Fallback (Catch-all for 404) */}
          <Route path="*" element={<NotFound />} />
          
        </Routes>
      </BrowserRouter>
    </PrimeReactProvider>
  </React.StrictMode>
);